import React, { useState, useEffect, useCallback, useMemo } from "react";
import API from "../../../api/api";
import { resolveAssetUrl } from "../../../api/config";
import {
  FaCalendarCheck,
  FaSun,
  FaMoon,
  FaUserFriends,
  FaCheck,
  FaTimes,
  FaClock,
  FaExclamationTriangle,
  FaFileUpload,
  FaFileAlt,
  FaDownload,
  FaPrint,
  FaHistory,
  FaCamera,
  FaRunning,
  FaClipboardCheck,
  FaSearch,
  FaRedoAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";
import FileUploadWithScanner from "../../../components/common/FileUploadWithScanner";
import { buildReportTable, outputCampReport } from "./reportOutput";

/* ─── tiny toast helper ─────────────────────────────────── */
const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
});

/* ─── IST date & time helpers ────────────────────────────── */
const getTodayISTDate = () => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());
};

const getCurrentISTTime = () => {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
};

/* ─── format time as HH:MM AM/PM ─────────────────────────── */
const fmtTime = (isoOrTime) => {
  if (!isoOrTime) return "";
  try {
    const d = isoOrTime.includes("T") ? new Date(isoOrTime) : new Date(`2000-01-01T${isoOrTime}`);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch {
    return isoOrTime;
  }
};

/* ─── status badge chip ────────────────────────────────────── */
const StatusChip = ({ status }) => {
  if (!status) return <span className="text-slate-400 text-xs">—</span>;
  const map = {
    present: "bg-emerald-100 text-emerald-800 border-emerald-300",
    absent: "bg-rose-100 text-rose-800 border-rose-300",
    late: "bg-amber-100 text-amber-800 border-amber-300",
    permission: "bg-indigo-100 text-indigo-800 border-indigo-300",
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${map[status] || "bg-slate-100 text-slate-600 border-slate-300"}`}>
      {status}
    </span>
  );
};

const CoachAttendance = () => {
  const [activeTab, setActiveTab] = useState("live"); // "live" | "upload"
  const todayIST = getTodayISTDate();
  const [selectedDate, setSelectedDate] = useState(() => getTodayISTDate());
  const [sheet, setSheet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRow, setSavingRow] = useState(null); // athlete_id being saved
  const [campId, setCampId] = useState(null);
  const [activeSession, setActiveSession] = useState("morning"); // "morning" | "evening"
  const isToday = selectedDate === todayIST;
  const isLocked = sheet.length > 0 && sheet.every((r) => r.is_locked);

  // Scan & Upload state
  const [uploadDate, setUploadDate] = useState(() => getTodayISTDate());
  const [uploadReportType, setUploadReportType] = useState("attendance"); // "attendance" | "workout_performance"
  const [workoutForm, setWorkoutForm] = useState({
    athlete_id: "",
    training_session: "both",
    activity_details: "",
    performance_rating: "Good",
    remarks: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingReport, setUploadingReport] = useState(false);
  const [uploadedReports, setUploadedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Search and Filter states
  const [archiveSearchTerm, setArchiveSearchTerm] = useState("");
  const [archiveCategoryFilter, setArchiveCategoryFilter] = useState("all");
  const [archiveDateFilter, setArchiveDateFilter] = useState("");

  const [athleteSearchTerm, setAthleteSearchTerm] = useState("");
  const [athleteStatusFilter, setAthleteStatusFilter] = useState("all"); // "all" | "present" | "absent" | "unmarked"

  const filteredUploadedReports = useMemo(() => {
    let list = uploadedReports || [];
    if (archiveSearchTerm && archiveSearchTerm.trim()) {
      const q = archiveSearchTerm.trim().toLowerCase();
      list = list.filter((r) =>
        (r.file_name && r.file_name.toLowerCase().includes(q)) ||
        (r.athlete_name && r.athlete_name.toLowerCase().includes(q))
      );
    }
    if (archiveCategoryFilter && archiveCategoryFilter !== "all") {
      list = list.filter((r) => r.report_type === archiveCategoryFilter);
    }
    if (archiveDateFilter) {
      list = list.filter((r) => {
        const d = r.date || r.report_date;
        return d && String(d).startsWith(archiveDateFilter);
      });
    }
    return list;
  }, [uploadedReports, archiveSearchTerm, archiveCategoryFilter, archiveDateFilter]);

  const filteredSheet = useMemo(() => {
    let list = sheet || [];
    if (athleteSearchTerm && athleteSearchTerm.trim()) {
      const q = athleteSearchTerm.trim().toLowerCase();
      list = list.filter((r) =>
        (r.athlete_name && r.athlete_name.toLowerCase().includes(q)) ||
        (r.name && r.name.toLowerCase().includes(q)) ||
        (r.bib_no && String(r.bib_no).toLowerCase().includes(q))
      );
    }
    if (athleteStatusFilter !== "all") {
      list = list.filter((r) => {
        const st = activeSession === "morning" ? r.morning_status : r.evening_status;
        if (athleteStatusFilter === "unmarked") return !st;
        return st === athleteStatusFilter;
      });
    }
    return list;
  }, [sheet, athleteSearchTerm, athleteStatusFilter, activeSession]);

  /* ─── Fetch sheet ─── */
  const fetchSheet = useCallback(async (date) => {
    setLoading(true);
    try {
      const res = await API.camp.coachPortal.getAttendanceSheet({ date });
      const data = res?.data || res;
      setSheet((data.sheet || []).map((r) => ({ ...r, _remarksInput: r.remarks || "" })));
      setCampId(data.camp_id);
    } catch (err) {
      console.error("Failed to load attendance sheet:", err);
      Swal.fire("Error", "Failed to load attendance sheet", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUploadedReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const res = await API.camp.coachPortal.getDailyAttendanceReports();
      const list = res?.data?.reports || res?.reports || res?.data || [];
      setUploadedReports(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load uploaded reports:", err);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  useEffect(() => {
    fetchSheet(selectedDate);
  }, [selectedDate, fetchSheet]);

  useEffect(() => {
    if (activeTab === "upload") {
      fetchUploadedReports();
    }
  }, [activeTab, fetchUploadedReports]);

  const handleUploadScannedSheet = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      Swal.fire("Missing File", "Please select a report file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("date", uploadDate);
    formData.append("file", selectedFile);
    formData.append("report_type", uploadReportType);
    if (campId) formData.append("camp_id", campId);

    if (uploadReportType === "workout_performance") {
      if (workoutForm.athlete_id) formData.append("athlete_id", workoutForm.athlete_id);
      if (workoutForm.training_session) formData.append("training_session", workoutForm.training_session);
      if (workoutForm.activity_details) formData.append("activity_details", workoutForm.activity_details);
      if (workoutForm.performance_rating) formData.append("performance_rating", workoutForm.performance_rating);
      if (workoutForm.remarks) formData.append("remarks", workoutForm.remarks);
    }

    setUploadingReport(true);
    try {
      const res = await API.camp.coachPortal.uploadDailyAttendanceReport(formData);
      Swal.fire({
        icon: "success",
        title: "Report Uploaded!",
        text: res?.data?.message || (uploadReportType === "workout_performance" ? "Daily workout & performance report uploaded successfully." : "End-of-day attendance sheet uploaded successfully."),
        timer: 2000,
        showConfirmButton: false,
      });
      setSelectedFile(null);
      setWorkoutForm({
        athlete_id: "",
        training_session: "both",
        activity_details: "",
        performance_rating: "Good",
        remarks: "",
      });
      // Reset file input element
      const fileInput = document.getElementById("scanned-sheet-file-input");
      if (fileInput) fileInput.value = "";
      fetchUploadedReports();
    } catch (err) {
      console.error("Upload error:", err);
      Swal.fire("Upload Failed", err?.response?.data?.message || "Failed to upload report.", "error");
    } finally {
      setUploadingReport(false);
    }
  };

  /* ─── Update a single row field in local state ─── */
  const updateRow = (athleteId, patch) => {
    setSheet((prev) =>
      prev.map((r) => (r.athlete_id === athleteId ? { ...r, ...patch } : r))
    );
  };

  /* ─── Save a single athlete's attendance (live) ─── */
  const saveSingleRow = useCallback(
    async (athleteId, overridePatch = {}) => {
      const row = sheet.find((r) => r.athlete_id === athleteId);
      if (!row) return;

      const merged = { ...row, ...overridePatch };
      setSavingRow(athleteId);
      try {
        const payload = {
          camp_id: campId,
          date: selectedDate,
          records: [
            {
              athlete_id: merged.athlete_id,
              coach_id: merged.coach_id,
              morning_in: merged.morning_in || null,
              morning_out: merged.morning_out || null,
              morning_status: merged.morning_status || null,
              evening_in: merged.evening_in || null,
              evening_out: merged.evening_out || null,
              evening_status: merged.evening_status || null,
              remarks: merged._remarksInput || merged.remarks || "",
            },
          ],
        };
        await API.camp.coachPortal.saveAttendance(payload);
        Toast.fire({ icon: "success", title: "Saved" });
      } catch (err) {
        Toast.fire({ icon: "error", title: err?.response?.data?.message || err?.message || "Save failed" });
      } finally {
        setSavingRow(null);
      }
    },
    [sheet, campId, selectedDate]
  );

  /* ─── Mark Present ─── */
  const markIn = (athleteId, session) => {
    const timeStr = getCurrentISTTime();
    const patch =
      session === "morning"
        ? { morning_status: "present", morning_in: isToday ? timeStr : undefined, morning_out: null }
        : { evening_status: "present", evening_in: isToday ? timeStr : undefined, evening_out: null };
    updateRow(athleteId, patch);
    saveSingleRow(athleteId, patch);
  };

  const markOut = (athleteId, session) => {
    const timeStr = getCurrentISTTime();
    const patch = session === "morning"
      ? { morning_out: isToday ? timeStr : undefined }
      : { evening_out: isToday ? timeStr : undefined };
    updateRow(athleteId, patch);
    saveSingleRow(athleteId, patch);
  };

  /* ─── Mark Absent ─── */
  const markAbsent = (athleteId, session) => {
    const patch = session === "morning"
      ? { morning_status: "absent", morning_in: null, morning_out: null }
      : { evening_status: "absent", evening_in: null, evening_out: null };
    updateRow(athleteId, patch);
    saveSingleRow(athleteId, patch);
  };

  /* ─── Save remarks (for absent) ─── */
  const saveRemarks = (athleteId) => {
    saveSingleRow(athleteId);
  };

  /* ─── Mark ALL ─── */
  const markAll = async (session, status) => {
    const sessionLabel = session === "morning" ? "Morning" : "Evening";
    const eligibleCount = sheet.filter((row) => {
      const inKey = `${session}_in`;
      const outKey = `${session}_out`;
      const statusKey = `${session}_status`;
      if (status === "out") {
        return row[statusKey] === "present" && Boolean(row[inKey]) && !row[outKey];
      }
      return status === "present"
        ? row[statusKey] !== "absent" && !row[inKey]
        : !row[inKey];
    }).length;

    if (eligibleCount === 0) {
      Toast.fire({
        icon: "info",
        title: status === "out" ? "No athletes are waiting to be marked Out" : "No eligible athletes found",
      });
      return;
    }

    const actionLabel = status === "present" ? "present" : status === "out" ? "out" : "absent";

    const confirmation = await Swal.fire({
      icon: status === "absent" ? "warning" : "question",
      title: status === "present"
        ? "Mark all athletes In?"
        : status === "out"
          ? "Mark all present athletes Out?"
          : "Mark all athletes absent?",
      html: `This will mark <strong>${eligibleCount}</strong> eligible athlete${eligibleCount === 1 ? "" : "s"} as <strong>${actionLabel}</strong> for the <strong>${sessionLabel}</strong> session on <strong>${selectedDate}</strong>.`,
      showCancelButton: true,
      confirmButtonText: status === "present"
        ? "Yes, mark all In"
        : status === "out"
          ? "Yes, mark all Out"
          : "Yes, mark absent",
      cancelButtonText: "Cancel",
      confirmButtonColor: status === "present" ? "#059669" : status === "out" ? "#0891b2" : "#e11d48",
      reverseButtons: true,
      focusCancel: status === "absent",
    });

    if (!confirmation.isConfirmed) return;

    const timeStr = getCurrentISTTime();
    const updatedSheet = sheet.map((r) => {
      const inKey = `${session}_in`;
      const outKey = `${session}_out`;
      const statusKey = `${session}_status`;
      if (status === "out") {
        if (r[statusKey] !== "present" || !r[inKey] || r[outKey]) return r;
        return { ...r, [outKey]: timeStr };
      }
      if (status === "present") {
        if (r[statusKey] === "absent" || r[inKey]) return r;
        return { ...r, [statusKey]: "present", [inKey]: isToday ? timeStr : r[inKey], [`${session}_out`]: null };
      }
      if (r[inKey]) return r;
      return { ...r, [statusKey]: "absent", [inKey]: null, [`${session}_out`]: null };
    });
    setSheet(updatedSheet);
    setSavingRow("__all__");
    try {
      await API.camp.coachPortal.saveAttendance({
        camp_id: campId,
        date: selectedDate,
        records: updatedSheet.map((r) => ({
          athlete_id: r.athlete_id,
          coach_id: r.coach_id,
          morning_in: r.morning_in || null,
          morning_out: r.morning_out || null,
          morning_status: r.morning_status || null,
          evening_in: r.evening_in || null,
          evening_out: r.evening_out || null,
          evening_status: r.evening_status || null,
          remarks: r._remarksInput || r.remarks || "",
        })),
      });
      Toast.fire({
        icon: "success",
        title: status === "present"
          ? "All eligible athletes marked In"
          : status === "out"
            ? "All present athletes marked Out"
            : "All eligible athletes marked Absent",
      });
    } catch (err) {
      Toast.fire({ icon: "error", title: err?.response?.data?.message || err?.message || "Bulk save failed" });
    } finally {
      setSavingRow(null);
    }
  };

  /* ─── KPI counts ─── */
  const morningPresent = sheet.filter((r) => r.morning_status === "present").length;
  const eveningPresent = sheet.filter((r) => r.evening_status === "present").length;
  const morningAbsent = sheet.filter((r) => r.morning_status === "absent").length;
  const eveningAbsent = sheet.filter((r) => r.evening_status === "absent").length;

  const outputFullTeamAttendance = (action) => outputCampReport({
    title: "Full Team Attendance Report",
    action,
    body: buildReportTable({
      heading: "Full Team Attendance Report",
      subtitle: `Date: ${selectedDate} | All ${sheet.length} assigned athletes`,
      columns: [
        { key: "serial", label: "#" },
        { key: "athlete", label: "Athlete" },
        { key: "bib", label: "BIB" },
        { key: "discipline", label: "Discipline" },
        { key: "morning", label: "Morning" },
        { key: "morning_time", label: "Morning In / Out" },
        { key: "morning_signature", label: "Morning Signature" },
        { key: "evening", label: "Evening" },
        { key: "evening_time", label: "Evening In / Out" },
        { key: "evening_signature", label: "Evening Signature" },
        { key: "remarks", label: "Remarks" }
      ],
      rows: sheet.map((row, index) => ({
        serial: index + 1,
        athlete: row.athlete_name || row.name,
        bib: row.bib_no,
        discipline: row.discipline_name || "Para Athlete",
        morning: row.morning_status || "Unmarked",
        morning_time: [fmtTime(row.morning_in), fmtTime(row.morning_out)].filter(Boolean).join(" / ") || "-",
        morning_signature: " ",
        evening: row.evening_status || "Unmarked",
        evening_time: [fmtTime(row.evening_in), fmtTime(row.evening_out)].filter(Boolean).join(" / ") || "-",
        evening_signature: " ",
        remarks: row._remarksInput || row.remarks || "-"
      }))
    })
  });

  return (
    <div className="space-y-5">
      {/* ── Header Banner ── */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">
              Attendance Register
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5 tracking-tight flex items-center gap-2">
              <FaCalendarCheck className="text-cyan-700" /> Live Attendance
            </h1>
            <p className="text-xs text-slate-600 mt-1">
              Tap <span className="font-bold text-emerald-700">In</span>, <span className="font-bold text-cyan-700">Out</span>, or{" "}
              <span className="font-bold text-rose-700">Absent</span> — time is saved automatically in IST
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => outputFullTeamAttendance("print")} disabled={!sheet.length} className="px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"><FaPrint /> Print Full Team</button>
            <button type="button" onClick={() => outputFullTeamAttendance("download")} disabled={!sheet.length} className="px-3 py-2 bg-cyan-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"><FaDownload /> Download PDF</button>
            <input
              type="date"
              value={selectedDate}
              max={todayIST}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-white border border-slate-300 text-slate-800 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 font-semibold cursor-pointer shadow-sm"
            />
            {!isToday && (
              <span className="text-[10px] text-amber-700 font-bold bg-amber-100 border border-amber-300 px-2 py-1 rounded-lg">
                Past Date
              </span>
            )}
          </div>
        </div>
      </section>

      {/* ── Tabs Navigation: Live Attendance vs Scan & Upload ── */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab("live")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeTab === "live"
              ? "bg-cyan-700 text-white shadow-sm"
              : "text-slate-600 bg-white hover:bg-slate-50 border border-slate-200"
          }`}
        >
          <FaCalendarCheck /> Daily Live Marking
        </button>
        <button
          onClick={() => setActiveTab("upload")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeTab === "upload"
              ? "bg-emerald-700 text-white shadow-sm"
              : "text-slate-600 bg-white hover:bg-slate-50 border border-slate-200"
          }`}
        >
          <FaFileUpload /> Upload Reports (Attendance &amp; Workouts)
        </button>
      </div>

      {activeTab === "upload" ? (
        /* ─── End-of-Day Scanned Sheet & Workout Report Upload View ─── */
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm max-w-2xl mx-auto">
            {/* Toggle Report Type */}
            <div className="flex border-b border-slate-200 pb-3 mb-4 gap-2">
              <button
                type="button"
                onClick={() => setUploadReportType("attendance")}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
                  uploadReportType === "attendance"
                    ? "bg-cyan-700 text-white shadow-sm"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                <FaClipboardCheck /> End-of-Day Attendance Sheet
              </button>
              <button
                type="button"
                onClick={() => setUploadReportType("workout_performance")}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
                  uploadReportType === "workout_performance"
                    ? "bg-emerald-700 text-white shadow-sm"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                <FaRunning /> Daily Workouts &amp; Individual Performance
              </button>
            </div>

            <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
              {uploadReportType === "workout_performance" ? (
                <>
                  <FaRunning className="text-emerald-600" /> Upload Athlete Daily Workout &amp; Performance
                </>
              ) : (
                <>
                  <FaCamera className="text-cyan-700" /> Upload Signed &amp; Scanned Attendance Sheet
                </>
              )}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              {uploadReportType === "workout_performance"
                ? "Record daily training details, drills conducted, rating, and upload workout scorecards or videos for individual athletes."
                : "At the end of each training day, scan or photograph the physical attendance register signed by the coach and athletes."}
            </p>

            <form onSubmit={handleUploadScannedSheet} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Date <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="date"
                    value={uploadDate}
                    max={todayIST}
                    onChange={(e) => setUploadDate(e.target.value)}
                    required
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {uploadReportType === "workout_performance" ? (
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Athlete (Optional)
                    </label>
                    <select
                      value={workoutForm.athlete_id}
                      onChange={(e) => setWorkoutForm({ ...workoutForm, athlete_id: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Assigned Team / Entire Squad</option>
                      {sheet.map((r) => (
                        <option key={r.athlete_id} value={r.athlete_id}>
                          {r.athlete_name || r.name} ({r.bib_no || "BIB"})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <FileUploadWithScanner
                      label="Scanned Document (PDF/JPG/PNG)"
                      name="selectedFile"
                      accept=".pdf,image/*"
                      required
                      value={selectedFile}
                      onChange={(file) => setSelectedFile(file)}
                      scannerTitle="Scan Attendance Sheet"
                      targetFileName={`attendance_sheet_${selectedDate}.jpg`}
                    />
                  </div>
                )}
              </div>

              {uploadReportType === "workout_performance" && (
                <div className="space-y-3 bg-emerald-50/50 p-3.5 rounded-xl border border-emerald-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Training Session
                      </label>
                      <select
                        value={workoutForm.training_session}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, training_session: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="both">Both Sessions (Morning &amp; Evening)</option>
                        <option value="morning">Morning Session Only</option>
                        <option value="evening">Evening Session Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Performance Rating
                      </label>
                      <select
                        value={workoutForm.performance_rating}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, performance_rating: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="Outstanding">Outstanding</option>
                        <option value="Very Good">Very Good</option>
                        <option value="Good">Good</option>
                        <option value="Average">Average</option>
                        <option value="Needs Improvement">Needs Improvement</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Workout Details &amp; Drill Activities
                    </label>
                    <textarea
                      rows={2}
                      value={workoutForm.activity_details}
                      onChange={(e) => setWorkoutForm({ ...workoutForm, activity_details: e.target.value })}
                      placeholder="e.g. 10km endurance cycle, 100m sprint drill sets, agility ladder work..."
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Coach Remarks
                    </label>
                    <input
                      type="text"
                      value={workoutForm.remarks}
                      onChange={(e) => setWorkoutForm({ ...workoutForm, remarks: e.target.value })}
                      placeholder="Remarks on effort, posture, stamina..."
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <FileUploadWithScanner
                      label="Attachment / Workout Sheet (PDF/JPG/PNG/DOCX)"
                      name="workoutFile"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx"
                      required
                      value={selectedFile}
                      onChange={(file) => setSelectedFile(file)}
                      scannerTitle="Scan Workout Sheet"
                      targetFileName={`workout_sheet_${selectedDate}.jpg`}
                    />
                  </div>
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={uploadingReport || !selectedFile}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition shadow-sm disabled:opacity-50 text-white ${
                    uploadReportType === "workout_performance"
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-cyan-700 hover:bg-cyan-800"
                  }`}
                >
                  <FaFileUpload />
                  {uploadingReport ? "Uploading Report..." : uploadReportType === "workout_performance" ? "Upload Workout Report" : "Upload Daily Sheet"}
                </button>
              </div>
            </form>
          </div>

          {/* Uploaded Sheets Archive */}
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h4 className="text-xs font-bold text-slate-800 flex items-center gap-2">
                <FaHistory className="text-cyan-700" /> Uploaded Reports Archive
                <span className="text-[10px] text-slate-500 font-normal">
                  ({filteredUploadedReports.length} of {uploadedReports.length})
                </span>
              </h4>

              {/* Filters Toolbar */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search file, athlete..."
                    value={archiveSearchTerm}
                    onChange={(e) => setArchiveSearchTerm(e.target.value)}
                    className="bg-white border border-slate-300 rounded-xl px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-cyan-500 shadow-xs w-40"
                  />
                  {archiveSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setArchiveSearchTerm("")}
                      className="absolute right-2 top-1.5 text-xs text-slate-400 hover:text-slate-700 font-bold"
                      title="Clear search"
                    >
                      &times;
                    </button>
                  )}
                </div>

                {/* Category Filter */}
                <select
                  value={archiveCategoryFilter}
                  onChange={(e) => setArchiveCategoryFilter(e.target.value)}
                  className="bg-white border border-slate-300 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-cyan-500 shadow-xs"
                >
                  <option value="all">All Categories</option>
                  <option value="attendance">Attendance Sheets</option>
                  <option value="workout_performance">Workout &amp; Performance</option>
                </select>

                {/* Date Filter */}
                <input
                  type="date"
                  value={archiveDateFilter}
                  onChange={(e) => setArchiveDateFilter(e.target.value)}
                  className="bg-white border border-slate-300 rounded-xl px-2 py-1 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-cyan-500 shadow-xs cursor-pointer"
                />
                {archiveDateFilter && (
                  <button
                    type="button"
                    onClick={() => setArchiveDateFilter("")}
                    className="text-xs text-slate-400 hover:text-slate-700 font-bold px-1"
                    title="Clear date"
                  >
                    &times;
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setArchiveSearchTerm("");
                    setArchiveCategoryFilter("all");
                    setArchiveDateFilter("");
                    fetchUploadedReports();
                  }}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-1 transition"
                  title="Reset filters & reload"
                >
                  <FaRedoAlt className={loadingReports ? "animate-spin" : ""} /> Refresh
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200">
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Category</th>
                    <th className="py-3 px-4">Athlete / Target</th>
                    <th className="py-3 px-4">File</th>
                    <th className="py-3 px-4">Uploaded At</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loadingReports ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        Loading uploaded reports archive...
                      </td>
                    </tr>
                  ) : filteredUploadedReports.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        {uploadedReports.length > 0
                          ? "No reports match your search or filter options."
                          : "No reports uploaded yet."}
                      </td>
                    </tr>
                  ) : (
                    filteredUploadedReports.map((rep) => {
                      const fileUrl = rep.file_url || (rep.file_path ? resolveAssetUrl(rep.file_path) : null);
                      return (
                        <tr key={rep.id || rep.file_name} className="hover:bg-slate-50 transition">
                          <td className="py-3 px-4 font-bold text-slate-900 font-mono">
                            {rep.date || rep.report_date}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                              rep.report_type === "workout_performance"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                : "bg-cyan-50 text-cyan-700 border-cyan-300"
                            }`}>
                              {rep.report_type === "workout_performance" ? "Workout & Performance" : "Attendance Sheet"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-700">
                            {rep.athlete_name ? (
                              <span className="font-bold text-slate-900">{rep.athlete_name}</span>
                            ) : (
                              <span className="text-slate-400">Assigned Squad</span>
                            )}
                          </td>
                          <td className="py-3 px-4 font-medium text-slate-600 flex items-center gap-1.5">
                            <FaFileAlt className="text-cyan-600" />
                            {rep.file_name || rep.file_path?.split("/").pop() || "attendance_sheet"}
                          </td>
                          <td className="py-3 px-4 text-slate-500 font-mono text-[11px]">
                            {rep.created_at ? new Date(rep.created_at).toLocaleDateString("en-IN") : "—"}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {fileUrl && (
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-bold text-cyan-700 hover:text-cyan-900 bg-cyan-50 hover:bg-cyan-100 px-3 py-1.5 rounded-lg transition"
                              >
                                <FaDownload /> View Sheet
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
      <>
      {/* ── Locked Banner ── */}
      {isLocked && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 flex items-center gap-3.5 text-amber-950 shadow-sm animate-in fade-in">
          <div className="w-10 h-10 rounded-xl bg-amber-200 text-amber-900 flex items-center justify-center text-lg font-bold shrink-0 shadow-xs">
            🔒
          </div>
          <div className="flex-1">
            <h4 className="font-extrabold text-xs sm:text-sm uppercase tracking-wider text-amber-900">
              Attendance Submitted &amp; Locked
            </h4>
            <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
              Attendance for this date has been submitted and locked for coach editing. If adjustments are required, please contact the Camp Administrator or Secretary.
            </p>
          </div>
        </div>
      )}

      {/* ── Session Toggle ── */}
      <div className="flex gap-2 bg-white border border-slate-200 p-1.5 rounded-2xl shadow-sm w-full sm:w-auto">
        <button
          onClick={() => setActiveSession("morning")}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeSession === "morning"
              ? "bg-amber-500 text-white shadow-sm"
              : "text-slate-600 hover:bg-amber-50"
          }`}
        >
          <FaSun /> Morning Session
        </button>
        <button
          onClick={() => setActiveSession("evening")}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeSession === "evening"
              ? "bg-indigo-600 text-white shadow-sm"
              : "text-slate-600 hover:bg-indigo-50"
          }`}
        >
          <FaMoon /> Evening Session
        </button>
      </div>

      {/* ── KPI Strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-3.5 shadow-sm text-center">
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
            <FaUserFriends /> Total
          </div>
          <div className="text-2xl font-black text-slate-900">{sheet.length}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 shadow-sm text-center">
          <div className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
            {activeSession === "morning" ? <FaSun /> : <FaMoon />} Present
          </div>
          <div className="text-2xl font-black text-emerald-700">
            {activeSession === "morning" ? morningPresent : eveningPresent}
          </div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3.5 shadow-sm text-center">
          <div className="text-[10px] text-rose-700 font-semibold uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
            <FaTimes /> Absent
          </div>
          <div className="text-2xl font-black text-rose-700">
            {activeSession === "morning" ? morningAbsent : eveningAbsent}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 shadow-sm flex flex-col items-center justify-center gap-2">
          <button
            onClick={() => markAll(activeSession, "present")}
            disabled={isLocked || savingRow === "__all__"}
            className="w-full px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-40"
          >
            <FaCheck /> Mark All In
          </button>
          <button
            onClick={() => markAll(activeSession, "out")}
            disabled={isLocked || !isToday || savingRow === "__all__"}
            className="w-full px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-40"
            title={!isToday ? "Bulk Out is available only for today's live attendance" : "Mark all athletes who are currently In as Out"}
          >
            <FaClock /> Mark All Out
          </button>
          <button
            onClick={() => markAll(activeSession, "absent")}
            disabled={isLocked || savingRow === "__all__"}
            className="w-full px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-40"
          >
            <FaTimes /> Mark All Absent
          </button>
        </div>
      </div>

      {/* ── Search & Filter Controls for Athlete Attendance ── */}
      {sheet.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1 max-w-sm">
              <input
                type="text"
                placeholder="Search athlete by name or BIB..."
                value={athleteSearchTerm}
                onChange={(e) => setAthleteSearchTerm(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-8 pr-7 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <span className="absolute left-2.5 top-2.5 text-slate-400 text-xs">
                <FaSearch />
              </span>
              {athleteSearchTerm && (
                <button
                  type="button"
                  onClick={() => setAthleteSearchTerm("")}
                  className="absolute right-2 top-2 text-xs text-slate-400 hover:text-slate-700 font-bold"
                >
                  &times;
                </button>
              )}
            </div>
            <span className="text-[11px] text-slate-500 font-medium whitespace-nowrap">
              Showing {filteredSheet.length} of {sheet.length}
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-bold text-slate-600 mr-1">Status:</span>
            {[
              { id: "all", label: "All" },
              { id: "present", label: "Present" },
              { id: "absent", label: "Absent" },
              { id: "unmarked", label: "Unmarked" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setAthleteStatusFilter(tab.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  athleteStatusFilter === tab.id
                    ? "bg-slate-900 text-white shadow-xs"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Athlete Cards ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sheet.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <FaCalendarCheck className="text-slate-400 text-4xl mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">No athletes assigned</p>
          <p className="text-xs text-slate-500 mt-1">
            Athletes assigned to your profile will appear here.
          </p>
        </div>
      ) : filteredSheet.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <p className="text-sm font-semibold text-slate-700">No athletes match your search or status filter</p>
          <button
            type="button"
            onClick={() => { setAthleteSearchTerm(""); setAthleteStatusFilter("all"); }}
            className="mt-2 text-xs font-bold text-cyan-700 hover:underline"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSheet.map((row) => {
            const isSaving = savingRow === row.athlete_id;
            const sessionStatus = activeSession === "morning" ? row.morning_status : row.evening_status;
            const sessionTime = activeSession === "morning" ? row.morning_in : row.evening_in;
            const sessionOutTime = activeSession === "morning" ? row.morning_out : row.evening_out;
            const isPresent = sessionStatus === "present";
            const isAbsent = sessionStatus === "absent";
            const needsRemarks = isAbsent && !(row._remarksInput || row.remarks);

            return (
              <div
                key={row.athlete_id}
                className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-all ${
                  isPresent
                    ? "border-emerald-200 ring-1 ring-emerald-100"
                    : isAbsent
                    ? "border-rose-200 ring-1 ring-rose-100"
                    : "border-slate-200"
                }`}
              >
                {/* Card Header */}
                <div className="flex items-start gap-3 p-4">
                  {/* Athlete Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-sm leading-tight">
                        {row.athlete_name}
                      </span>
                      <span className="text-[10px] font-mono font-semibold bg-cyan-50 text-cyan-800 border border-cyan-200 px-2 py-0.5 rounded-full">
                        {row.bib_no}
                      </span>
                      {row.category && (
                        <span className="text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">
                          {row.category}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                      {row.discipline_name || "Para Athlete"}
                    </p>

                    {/* Status & Time display */}
                    <div className="flex items-center gap-3 mt-2">
                      <StatusChip status={activeSession === "morning" ? row.morning_status : row.evening_status} />
                      {sessionTime && (
                        <span className="flex items-center gap-1 text-[10px] text-slate-500 font-medium">
                          <FaClock className="text-[9px]" />
                          In {fmtTime(sessionTime)}{sessionOutTime ? ` · Out ${fmtTime(sessionOutTime)}` : ""}
                        </span>
                      )}
                      {isSaving && (
                        <span className="text-[10px] text-cyan-600 font-semibold animate-pulse">
                          Saving…
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    <button
                      onClick={() => markIn(row.athlete_id, activeSession)}
                      disabled={isLocked || isSaving || Boolean(sessionTime)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm min-w-[90px] justify-center ${
                        isPresent
                          ? "bg-emerald-600 text-white ring-2 ring-emerald-400"
                          : "bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-600 hover:text-white"
                      } disabled:opacity-50`}
                    >
                      <FaCheck /> In
                    </button>
                    <button
                      onClick={() => markOut(row.athlete_id, activeSession)}
                      disabled={isLocked || isSaving || isAbsent || !sessionTime || Boolean(sessionOutTime)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm min-w-[90px] justify-center ${
                        sessionOutTime
                          ? "bg-cyan-600 text-white ring-2 ring-cyan-400"
                          : "bg-cyan-50 text-cyan-800 border border-cyan-300 hover:bg-cyan-600 hover:text-white"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <FaClock /> Out
                    </button>
                    <button
                      onClick={() => markAbsent(row.athlete_id, activeSession)}
                      disabled={isLocked || isSaving || Boolean(sessionTime)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm min-w-[90px] justify-center ${
                        isAbsent
                          ? "bg-rose-600 text-white ring-2 ring-rose-400"
                          : "bg-rose-50 text-rose-800 border border-rose-300 hover:bg-rose-600 hover:text-white"
                      } disabled:opacity-50`}
                    >
                      <FaTimes /> Absent
                    </button>
                  </div>
                </div>

                {/* Remarks row (shown only if absent) */}
                {isAbsent && (
                  <div className={`px-4 pb-4 pt-1 border-t ${needsRemarks ? "border-rose-200 bg-rose-50/50" : "border-slate-100 bg-slate-50/40"}`}>
                    {needsRemarks && (
                      <div className="flex items-center gap-1.5 text-[10px] text-rose-700 font-bold mb-2">
                        <FaExclamationTriangle />
                        You may add a reason for absence
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Reason for absence (optional)"
                        disabled={isLocked}
                        value={row._remarksInput || ""}
                        onChange={(e) => updateRow(row.athlete_id, { _remarksInput: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && saveRemarks(row.athlete_id)}
                        className="flex-1 bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400 shadow-sm placeholder-slate-400 disabled:opacity-50"
                      />
                      <button
                        onClick={() => saveRemarks(row.athlete_id)}
                        disabled={isLocked || isSaving || !(row._remarksInput || "").trim()}
                        className="px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-40 whitespace-nowrap"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {/* Both sessions summary (compact bottom strip) */}
                <div className="flex items-center gap-4 px-4 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <FaSun className="text-amber-500" />
                    <span>Morning:</span>
                    <StatusChip status={row.morning_status} />
                    {row.morning_in && <span className="font-mono">{fmtTime(row.morning_in)}</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <FaMoon className="text-indigo-500" />
                    <span>Evening:</span>
                    <StatusChip status={row.evening_status} />
                    {row.evening_in && <span className="font-mono">{fmtTime(row.evening_in)}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default CoachAttendance;
